import React from 'react';

import { useIntl } from 'react-intl-next';

import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	UNSAFE_UpdateOnChange as PopoverUpdateOnChange,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';

import {
	useGettingStartedTourState,
	useGettingStartedTourStoreActions,
} from '../../controllers/getting-started-tour';

import { i18n } from './messages';

type GettingStartedTourSpotlightProps = {
	/** This is not the current step, but the step that the children should render **/
	step: 1 | 2 | 3;
	children?: any;
};

const stepConfig = [
	{
		title: i18n.stepOneTitle,
		description: i18n.stepOneDescription,
		placement: 'left-end',
	},
	{
		title: i18n.stepTwoTitle,
		description: i18n.stepTwoDescription,
		placement: 'left-end',
	},
	{
		title: i18n.stepThreeTitle,
		description: i18n.stepThreeDescription,
		placement: 'bottom-end',
	},
] as const;

export const GettingStartedTourSpotlight = ({
	children,
	step,
}: GettingStartedTourSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { stepNumber: currentStepNumber, totalSteps } = useGettingStartedTourState();
	const { dismiss, nextStep, previousStep } = useGettingStartedTourStoreActions();

	const currentConfig = stepConfig[step - 1];

	if (!fg('platform_aifc_getting_started_spotlight')) {
		return children;
	}

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange selectors={['[data-testid="smart-create-prompt"]']} />
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={() => {}}
				placement={currentConfig?.placement}
				isVisible={currentStepNumber === step}
			>
				<FadeIn entranceDirection="left">
					{(props) => (
						<div {...props}>
							<SpotlightCard testId="spotlight">
								<SpotlightHeader>
									<SpotlightHeadline>{formatMessage(currentConfig?.title)}</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl onClick={dismiss} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>{formatMessage(currentConfig?.description)}</SpotlightBody>
								<SpotlightFooter>
									<SpotlightStepCount>
										{formatMessage(i18n.stepIndicator, {
											stepNumber: step,
											totalSteps,
										})}
									</SpotlightStepCount>
									<SpotlightActions>
										{currentStepNumber > 1 && (
											<SpotlightSecondaryAction onClick={previousStep}>
												{formatMessage(i18n.back)}
											</SpotlightSecondaryAction>
										)}
										<SpotlightPrimaryAction onClick={nextStep}>
											{currentStepNumber < totalSteps
												? formatMessage(i18n.next)
												: formatMessage(i18n.done)}
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</div>
					)}
				</FadeIn>
			</PopoverContent>
		</PopoverProvider>
	);
};
